import React, { useEffect } from "react";
import { IconButton, Stack } from "@mui/material";
import { Delete } from "@mui/icons-material";
import { useFieldArray, useWatch } from "react-hook-form";
import { FormBuilder } from "components/FormBuilder/FormBuilder";
import { useTranslation } from "react-i18next";

/**
 * @param {Object} props 
 * @param {import("react-hook-form").UseFormReturn} props.formProps
 * @param {string} props.name
 * @param {boolean} props.disabled
 * @param {boolean} props.readOnly
 */
const ShareholderFields = ({
  formProps,
  name = "shareholderInfo",
  disabled,
  readOnly
}) => {
  const { t: tLocal } = useTranslation("translation", { keyPrefix: "component.form.shareholder" });
  const { control } = formProps;
  const { fields, append, remove } = useFieldArray({
    control,
    name,
  });
  const shareholderInfo = useWatch({ control: control, name });

  // Validate percentage field with total
  const validatePercentage = (value, index) => {
    const otherFieldsTotal = shareholderInfo?.reduce(
      (sum, item, idx) =>
        sum + (idx !== index ? Number(item.percentage) || 0 : 0),
      0,
    );
    const newTotal = Number(value) + otherFieldsTotal;
    return newTotal <= 100;
  };

  return (
    <FormBuilder.Section
      title={tLocal("title")}
      titleVariant="h3"
      padding="0px 16px 0px"
      extraConfigs={[
        !readOnly && {
          componentType: "button",
          config: {
            title: tLocal("addButton"),
            variant: "outline-normal-button",
            onClick: () => append({ name: "", idNo: "", percentage: "0" }),
            disabled: disabled,
          },
        },
      ]}>
      {fields.map((field, index) => {
        /** @type {Record<string, import("components/FormBuilder/lib/FormBuilderFieldBuilder").formBuilderFieldConfig>} */
        const fieldsConfig = {
          name: {
            fieldType: "textInput",
            fieldName: `${name}.${index}.name`,
            label: tLocal("columns.name"),
            mandatory: true,
            disabled: disabled,
            readOnly: readOnly,
          },
          idNo: {
            fieldType: "textInput",
            fieldName: `${name}.${index}.idNo`,
            label: tLocal("columns.idNo"),
            mandatory: true,
            disabled: disabled,
            readOnly: readOnly,
            config: {
              rules: ["validShareholderId"]
            },
          },
          percentage: {
            fieldType: "textInput",
            fieldName: `${name}.${index}.percentage`,
            label: tLocal("columns.percentage"),
            mandatory: true,
            disabled: disabled,
            readOnly: readOnly,
            config: {
              inputType: "percentage",
              numberRange: [0, 100],
              defaultValue: 0,
              asNumber: true,
              rules: ["onlyPositiveNumber"],
              sanitizers: ["decimal:2", "range:[0,100]"],
              customRule: {
                rule: (value) => validatePercentage(value, index),
                errorMessage: tLocal("error.percentage"),
              }
            },
          },
        }

        return (
          <Stack
            key={field.id}
            direction="row"
            spacing={2}
            alignItems="center"
          >
            {FormBuilder.buildFields({
              formProps,
              fields: [fieldsConfig.name, fieldsConfig.idNo, fieldsConfig.percentage],
            })}
            {!readOnly && <IconButton
              size="small"
              onClick={() => fields.length > 1 && remove(index)}
              sx={{
                color: "error.main",
                height: 40,
                width: 40, // Make it square
                borderRadius: "50%", // Make it circular
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)", // Light gray hover effect
                  borderRadius: "50%", // Maintain circular shape on hover
                },
              }}
              disabled={disabled || fields.length === 1}
            >
              <Delete fontSize="small" />
            </IconButton>}
          </Stack>
        )
      })}
    </FormBuilder.Section>
  );
}

export default React.memo(ShareholderFields);